import React from "react";
import { useTranslation } from "@emisgroup/acp-application-intl";
import {
  ArtefactPicker,
  ArtefactTypes,
  Environments,
  SelectedArtefactType
} from "@emisgroup/cohort-builder-kit";
import { useSelector } from "react-redux";
import { Actions } from "../OutputMappingButtonsBar/Types";
import { OutputConfigEditRootMap, OutputConfigRootMap } from "../../Api/Types";
import { ButtonVariantType, InputType } from "../../../../Common/UI/Types";
import {
  Button,
  Checkbox,
  Form,
  FormSection,
  FormElement,
  FormButton,
  Input
} from "../../../../Common/UI/Index";
import { useUser } from "../../../../../AuthContext";
import { IRootState } from "../../../../../reducer/CombineReducer";
import { FEASIBILITY_APP_ID } from "../../../../LeftPanel/Constant";
import { SelectedReport } from "./Types";
import Picker from "~icons/ic/outline-apps";
import "../../Style.scss";

interface Props {
  /**
   * Action on pick report
   */
  onPickReport: () => void;
  /**
   * If the form is to create or modify
   */
  popupAction: string;
  /**
   * Falg to show the picker
   */
  showPicker: boolean;
  /**
   * Action on pick report
   */
  onPickReportClose: () => void;
  /**
   * Selected report callback for the picker
   */
  selectedReport: (report: SelectedReport) => void;
  /**
   * Selected report data
   */
  selectedReportData: SelectedReport;
  /**
   * Flag - if its child item or not
   */
  isChildItem: boolean;
  /**
   * Method for config detils onChange
   */
  onChange: (e: React.SyntheticEvent<any, Event>) => void;
  /**
   * Local state for cofig details create
   */
  createData: OutputConfigRootMap;
  /**
   * Local state for cofig details edit
   */
  editData: OutputConfigEditRootMap;
  /**
   * Config tag name
   */
  headerText: string;
  /**
   * On include xml change
   */
  onIncludeXMLChange: (name: string) => ((isChecked: boolean) => void);
}

/**
 * Responsible to render config details popup view
 */
export const ConfigDetailsFormView: React.FC<Props> = (props: Props) => {
  const {
    onPickReport,
    popupAction,
    showPicker,
    selectedReport,
    isChildItem,
    onChange,
    createData,
    editData,
    onPickReportClose,
    onIncludeXMLChange,
  } = props;
  const { t: translate } = useTranslation();
  const isCreate = (popupAction === Actions.CREATE);
  const { user } = useUser();
  const token = user.token;
  const partition = useSelector((state: IRootState) => state.partition);
  const filteredPartition = partition.artefacts.filter(
    (artefact: SelectedArtefactType) => artefact.id === partition.selectedId as string);

  /**
   * Method to handle when selecting the artefact from the picker
   * @param artefact selected from picker
   */
  const handleChosenArtefact = (artefact: SelectedArtefactType): void => {
    const report: SelectedReport = {
      reportName: artefact.name,
      reportERN: artefact.id
    };
    selectedReport(report);
  };

  return (
    <>
      <Form>
        <FormSection
          id="ebt-outputConfig-config-configDetails-form">
          <FormElement
            id="ebt-outputConfig-config-configDetails-form-configName"
            fieldId="configName"
            labelText={translate("OutputConfig.OutputMappingConfigDetailsForm.ConfigName")}
            mandatory={true}
          >
            <Input
              id="ebt-outputConfig-config-configDetails-configName"
              type={InputType.Text}
              name="configName"
              value={isCreate ? createData.configName : editData.configName}
              onChange={onChange}
            />
          </FormElement>
          <FormElement
            fieldId="tag"
            labelText={translate("OutputConfig.OutputMappingConfigDetailsForm.Tag")}
            mandatory={true}
            id="ebt-outputConfig-config-configDetails-form-tagName"
          >
            <Input
              id="ebt-outputConfig-config-configDetails-tagName"
              type={InputType.Text}
              name="tag"
              value={isCreate ? createData.tag : editData.tag}
              onChange={onChange}
            />
          </FormElement>
        </FormSection>

        {
          !isChildItem &&
          <FormSection
            data-testid="output-config-formsection"
            horizontal={true}
            className="ebt-config-details"
          >

            <FormElement
              data-testid="ebt-outputConfig-config-configDetails-form"
              fieldId="sourceReport"
              labelText={translate("OutputConfig.OutputMappingConfigDetailsForm.SourceReport")}
              mandatory={true}
            >
              <Input
                id="ebt-outputConfig-config-sourceReport"
                type={InputType.Text}
                name="sourceReport"
                value={isCreate ?
                  createData?.sourceReport?.reportName :
                  editData?.sourceReport?.reportName
                }
                onChange={onChange}
                disabled={true}
              />
            </FormElement>
            <FormButton
              id="ebt-outputConfig-config-configDetails-formButtons"
            >
              <Button
                variant={ButtonVariantType.FILLED}
                onClick={onPickReport}
                className="ebt-mapping__picker_button">
                <Picker title="picker" />
              </Button>
            </FormButton>
          </FormSection>
        }
        <FormSection>
          <FormElement>
            <Checkbox
              className="ebt-mapping__include-in-xml"
              id="ebt-outputConfig-config-includeOutput"
              labelText={translate("OutputConfig.OutputMappingAgeForm.IncludeInOutput")}
              name="includeInOutput"
              checked={isCreate ? createData.includeInOutput : editData.includeInOutput}
              onChange={onIncludeXMLChange("includeInOutput")}
            />
          </FormElement>
        </FormSection>
      </Form>
      {showPicker &&
        <div className="ebt-config-details-picker">
          <ArtefactPicker
            artefacts={filteredPartition}
            type={[ArtefactTypes.PARTITION, ArtefactTypes?.SEARCH, ArtefactTypes?.REPORT]}
            selectableType={[ArtefactTypes?.REPORT, ArtefactTypes?.SEARCH]}
            selectableLevel={[0]}
            level={1000}
            closeDialogTrigger={(): void => onPickReportClose()}
            env={process.env.APP_TARGET_ENV as Environments}
            accessToken={token}
            chooseButtonText="Choose Report"
            cancelButtonText="Cancel"
            chosenArtefact={handleChosenArtefact}
            openModal={showPicker}
            pickerTitle="Report Picker"
            partitionTitle="Partition"
            isToShowFullScreen={true}
            applicationId={FEASIBILITY_APP_ID}
          />
        </div>
      }
    </>
  );
};
